import { IDocumentAssemblerGenerationDecision } from '@commenda/api';
import type { ColumnGroup } from '@commenda/kakashi';
import { Badge, DataTable, Tooltip } from '@commenda/kakashi';

import type { DocumentAssemblerBadgeMeta } from './document-assembler.types';

interface DocumentAssemblerGenerationDecisionsTableProps {
  decisions: IDocumentAssemblerGenerationDecision[];
}

const getBranchBadge = (
  decision: IDocumentAssemblerGenerationDecision
): DocumentAssemblerBadgeMeta =>
  decision.branchId === null
    ? { variant: 'gray', label: 'None — passage absent' }
    : { variant: 'blue', label: decision.kind ?? 'No longer in this version' };

export const DocumentAssemblerGenerationDecisionsTable = ({
  decisions,
}: DocumentAssemblerGenerationDecisionsTableProps) => {
  const columnGroups: ColumnGroup<
    IDocumentAssemblerGenerationDecision,
    unknown
  >[] = [
    {
      id: 'decisions',
      columns: [
        {
          id: 'block',
          accessorFn: (decision) => decision.conditionalTreeOrdinal,
          title: 'Block',
          header: 'Block',
          width: 96,
          cell: ({ row }) =>
            row.original.conditionalTreeOrdinal === -1 ? (
              <Tooltip content="The block this decision referred to is no longer in this version.">
                <span className="txt-extra-small-regular text-text-tertiary">
                  Unknown
                </span>
              </Tooltip>
            ) : (
              <span className="txt-extra-small-regular text-text-tertiary">
                #{row.original.conditionalTreeOrdinal + 1}
              </span>
            ),
        },
        {
          id: 'branch',
          accessorFn: (decision) => getBranchBadge(decision).label,
          title: 'Branch that won',
          header: 'Branch that won',
          width: 160,
          cell: ({ row }) => {
            const badge = getBranchBadge(row.original);

            return (
              <Badge variant={badge.variant} size="sm">
                {badge.label}
              </Badge>
            );
          },
        },
        {
          id: 'condition',
          accessorFn: (decision) => decision.condition ?? '',
          title: 'Its condition',
          header: 'Its condition',
          cell: ({ row }) => (
            <span className="txt-small-regular whitespace-pre-wrap text-text-secondary">
              {row.original.condition ?? '—'}
            </span>
          ),
        },
      ],
    },
  ];

  return (
    <div className="flex flex-col gap-sm">
      <DataTable columnGroups={columnGroups} data={decisions} />
      <p className="txt-extra-small-regular text-text-tertiary">
        What each branch actually says is not part of this record — only which
        one was chosen.
      </p>
    </div>
  );
};
